import React, { Suspense, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { getCookie } from 'cookies-next';
import { LineItem } from 'shared/types/cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import CustomerOrderSummary from '@/components/customercenter-ui/components/cart/components/OrderSummary';
import PaymentGuardian from '@/components/customercenter-ui/components/customer-detail/components/PaymentGuardian/paymentGuardian';
import { RESPONSE_SUCCESS_MESSAGE } from '@/components/customercenter-ui/components/customer-detail/constants';
import { PaymentGuardianData } from '@/components/customercenter-ui/components/customer-detail/types';
import { transformCustomerDetailsData } from '@/components/customercenter-ui/components/customer-detail/utils';
import { PageTexts } from '@/components/customercenter-ui/components/order-details/i18n';
import { getContentByTitle } from '@/components/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import BackButton from '@/components/customercenter-ui/components/ui/back-button';
import { hidePaymentGuardianSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { CreditScenarios, useCheckout } from '@/context/checkoutProvider';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { PathNames, getPageUrl, getSelectedDivision, TranslateText } from '@/helpers/utils/siteUtils';
import OrderLineItem from './components/OrderLineItem';
import { OrderCancelBox, OrderCancelMessage } from './messages/OrderCancel';
import { OrderConfirmMessage } from './messages/OrderConfimation';
import { OrderDownPaymentPendingBox, OrderDownPaymentPendingMessage } from './messages/OrderDownPaymentPending';
import { OrderApprovalPendingMessage, OrderPendingApprovalBox } from './messages/OrderPendingApproval';
import OrderPayment from './orderPayment';
import OrderShippingAddress from './shippingAddress';
import { BRAND_TEXT_TO_REPLACE, COOKIE_KEYS, DEFAULT_SITE_LANG, ORDER_TYPES } from '../../../../../constants/constant';
import Loader from '../../../../customercenter-ui/components/ui/loader';
const CreditSummary = dynamic(
  () => import('@/components/customercenter-ui/components/customer-detail/components/CreditSummary/creditSummary'),
  {
    ssr: false,
    loading: () => null,
  },
);
const OrderConfirmation = ({ order: orderData, paymentGuardianData, creditAccountNumber }: any) => {
  const { setCreditDecision, creditDecision } = useCheckout();
  const { resetCart } = useCart(false);
  const { getCustomerDetails } = useAccount();
  const projectSettings: ProjectSettings = useProjSettings();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [downpaymentSuccess, setDownPaymentSuccess] = useState<boolean>(false);
  const [showPaymentGuardianModal, setShowPaymentGuardianModal] = useState<boolean>(false);
  const [customerDetailsResponse, setCustomerDetailsResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [formattedPaymentGuardianData, setFormattedPaymentGuardianData] = useState<PaymentGuardianData>(
    {} as PaymentGuardianData,
  );
  const [siteId, setSiteId] = useState<string>();
  const [backUrl, setBackUrl] = useState<string>();
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');
  const currentSiteName = siteId && (mapLocaleToMeaningfulFormat() as { [key: string]: any })[siteId];
  const debtProtectionEnabled = siteId && (projectSettings?.debtProtectionEnabled?.[`${siteId?.slice(0, 2)}`] ?? false);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const siteRetailerMonikers = projectSettings?.siteRetailerMonikers;
  const retailerMoniker = siteRetailerMonikers[siteId ?? 'ZBSite'];

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
    const backUrl = getPageUrl(PathNames.CUSTOMER_DETAILS) ?? '';
    setBackUrl(backUrl);
  }, []);

  useEffect(() => {
    if (currentSiteName) {
      getContentByTitle('learnMore').then((data: any) => {
        setFormattedPaymentGuardianData((prev) => ({
          ...prev,
          learnMore:
            data?.htmlContentEntries?.[0]?.rich_text_and_html_editor?.replaceAll(
              BRAND_TEXT_TO_REPLACE,
              currentSiteName,
            ) || '',
        }));
      });

      getContentByTitle('paymentGuardianAgreement').then((data: any) => {
        setFormattedPaymentGuardianData((prev) => ({
          ...prev,
          paymentGuardianAgreement:
            data?.htmlContentEntries?.[0]?.rich_text_and_html_editor?.replaceAll(
              BRAND_TEXT_TO_REPLACE,
              currentSiteName,
            ) || '',
        }));
      });
    }
  }, [currentSiteName]);

  useEffect(() => {
    if (currentSiteName) {
      setFormattedPaymentGuardianData({
        benefitsDirect: paymentGuardianData.benefitsDirect?.replaceAll(BRAND_TEXT_TO_REPLACE, currentSiteName),
        customerCare: paymentGuardianData.customerCare?.replaceAll(BRAND_TEXT_TO_REPLACE, currentSiteName),
        paymentGuardianSuccessMessage: paymentGuardianData.paymentGuardianSuccessMessage?.replaceAll(
          BRAND_TEXT_TO_REPLACE,
          currentSiteName,
        ),
        paymentGuardianErrorMessage: paymentGuardianData?.paymentGuardianErrorMessage,
      });
    }
  }, [paymentGuardianData, currentSiteName]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value) => {
      let decision = '';
      if (value === 'on') decision = CreditScenarios.DownPaymentNeeded;
      else if (value === 'approved') decision = CreditScenarios.Approved;
      else if (value === 'pending') decision = CreditScenarios.Pending;
      setCreditDecision(decision);
    });
  }, [creditDecision]);

  const handleDownpaymentSuccess = (success: boolean) => {
    setDownPaymentSuccess(success);
  };

  const { creditProtectionInfo, isEligible, isEnrolled } = transformCustomerDetailsData({
    customerDetails: customerDetailsResponse,
  } as any);

  const getCustomerDetailsLocally = async () => {
    setLoading(true);
    try {
      const data = JSON.parse(getCookie(COOKIE_KEYS.CDP_URL) ?? '');
      const customerId = data?.customerId ?? '';
      const divisionCode = data?.divisionCode ?? '';
      const customerDetailsResponse = await getCustomerDetails({
        query: {
          customerId: customerId,
          divisionCode: divisionCode,
          creditAccountId: '',
          mailingRecordId: '',
          idName: 'customerId',
        },
      });

      if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
        setCustomerDetailsResponse(customerDetailsResponse);
        const isSoldAccount =
          customerDetailsResponse?.Customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ?? false;
        setIsSoldAccount(isSoldAccount);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentSiteName && debtProtectionEnabled && isEligible && !isEnrolled && formattedPaymentGuardianData) {
      if (formattedPaymentGuardianData?.learnMore && formattedPaymentGuardianData?.paymentGuardianAgreement) {
        setShowPaymentGuardianModal(true);
      }
    }
  }, [debtProtectionEnabled, isEligible, isEnrolled, formattedPaymentGuardianData, currentSiteName]);

  useEffect(() => {
    if (downpaymentSuccess) {
      setCreditDecision('Approved');
    }
  }, [downpaymentSuccess]);

  useEffect(() => {
    const getData = async () => {
      await getCustomerDetailsLocally();
    };
    getData();
  }, []);

  useEffect(() => {
    const divisionCode = getSelectedDivision() ?? 'ZB';
    const resetCartData = async () => {
      await resetCart({
        orderType: ORDER_TYPES.NEW_ORDER,
        divisionCode: divisionCode,
      });
    };
    resetCartData();
  }, []);

  useEffect(() => {
    if (orderData?.lineItems && siteId) {
      const insuranceLineItem: LineItem | undefined = orderData?.lineItems?.find(
        (item: LineItem) => item?.isInsuranceItem,
      );
      if (typeof window === 'undefined') return;
      const SDK = window.Narvar?.ShippingProtection;
      if (!SDK) {
        return;
      }
      let omsCustomerId = '';
      const current_CDP_Data = getCookie(COOKIE_KEYS.CDP_URL) ?? '';
      if (current_CDP_Data.length) {
        const data = JSON.parse(current_CDP_Data);
        omsCustomerId = data?.omsCustomerId;
      }
      const handleConfirmOrderToNarvar = async () => {
        await SDK.init({
          retailerMoniker: retailerMoniker,
          placement: 'order-confirm',
          locale: 'en-US',
          mode: 'reporter',
          sessionId: orderData?.orderNumber,
          ...(omsCustomerId && { customerId: omsCustomerId }),
        });

        SDK.report('order-confirm', {
          orderId: orderData?.orderNumber,
        });
      };
      if (insuranceLineItem) {
        handleConfirmOrderToNarvar();
      }
      sessionStorage.setItem('openedOnceForOrder', 'false');
    }
  }, [orderData, retailerMoniker, siteId]);

  if (!orderData) {
    return <></>;
  }

  const renderOrderMessage = () => {
    switch (creditDecision) {
      case CreditScenarios.Pending:
        return <OrderApprovalPendingMessage order={orderData} />;
      case CreditScenarios.Denied:
        return <OrderCancelMessage order={orderData} />;
      case CreditScenarios.DownPaymentNeeded:
        return <OrderDownPaymentPendingMessage order={orderData} />;
      default:
        return <OrderConfirmMessage order={orderData} />;
    }
  };
  const renderOrderComponent = () => {
    switch (creditDecision) {
      case CreditScenarios.Pending:
        return <OrderPendingApprovalBox />;
      case CreditScenarios.Denied:
        return <OrderCancelBox />;
      case CreditScenarios.DownPaymentNeeded:
        return (
          <>
            {!downpaymentSuccess && (
              <OrderDownPaymentPendingBox
                order={orderData}
                handleDownpaymentSuccess={handleDownpaymentSuccess}
                loading={loading}
                customerDetails={customerDetailsResponse}
              ></OrderDownPaymentPendingBox>
            )}
          </>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <section className="max-w-screen-9xl">
        <section className="flex flex-col  md:flex-row">
          {loading && <Loader />}

          <section className="w-full md:px-4">
            {renderOrderMessage()}
            {orderData?.email && (
              <p className="pt-3 text-base font-normal text-black">
                {mapLocaleToMeaningfulFormat().confirmationEmailMsg1}
                <a href={`mailto:${orderData?.email}`} className="gbmask font-bold">
                  {orderData?.email}
                </a>{' '}
                {mapLocaleToMeaningfulFormat().confirmationEmailMsg2}
                {mapLocaleToMeaningfulFormat().emailMsg1}{' '}
              </p>
            )}

            {backUrl && (
              <BackButton
                text={Texts.goToCustomerDetails}
                className={`"my-4 my-4 flex rounded-sm bg-deepBlue p-3 text-xl font-semibold text-white`}
                url={backUrl}
                showBackArrow={false}
              />
            )}

            {renderOrderComponent()}
            <section className="flex gap-x-4">
              <OrderShippingAddress order={orderData} />
              <OrderPayment order={orderData} creditAccountNumber={creditAccountNumber} />
            </section>
            <section className="my-4 rounded-md border border-foggyGray shadow-lg">
              <p className="p-4 text-2xl font-bold">
                {orderData?.lineItems?.filter((item: LineItem) => !item.isInsuranceItem).length ?? 0} Items in This
                Order
              </p>
              {orderData?.lineItems
                ?.filter((lineItem: LineItem) => !lineItem.isInsuranceItem)
                .map((item: LineItem) => (
                  <OrderLineItem
                    cartItem={item}
                    key={item?.lineItemId}
                    orderType={orderData?.orderType}
                    orderLineItems={orderData?.lineItems}
                  ></OrderLineItem>
                ))}
            </section>
            {showPaymentGuardianModal && !hidePaymentGuardianSection(projectSettings, isSoldAccount) && (
              <PaymentGuardian
                creditProtectionInfo={creditProtectionInfo}
                paymentGuardianData={formattedPaymentGuardianData}
                onlyShowModal={true}
              />
            )}
          </section>
          <section className="w-full pr-4 md:max-w-[460px]">
            <section className="mt-8 flex justify-end pb-4">
              {backUrl && (
                <BackButton
                  text={Texts.backToCustomerDetails}
                  className={`flex text-blueLight underline`}
                  url={backUrl}
                />
              )}
            </section>

            <CustomerOrderSummary
              Texts={Texts}
              orderData={orderData}
              pageName="orderConfirmation"
            ></CustomerOrderSummary>
            <Suspense
              fallback={
                <div>
                  <Loader />
                </div>
              }
            >
              <div className="my-3">{allowedCreditSites?.creditSites?.includes(siteId) && <CreditSummary />}</div>
            </Suspense>
          </section>
        </section>
      </section>
    </>
  );
};

export default OrderConfirmation;
